import { useEffect, useRef, type Dispatch, type MutableRefObject } from "react";
import type { ConnectionState } from "../../../lib/styxClient";
import type { MessageRow } from "../../../lib/db";
import {
  getOutboxEntry,
  listOutboxEntriesSorted,
  mergeMessagePatch,
  saveOutboxEntry,
  MAX_OUTBOX_FLUSH_ATTEMPTS,
} from "../../../lib/db/messages";
import { vaultPeerKey } from "../../../lib/crypto";
import { workspaceTopicPeerKey } from "../../../lib/workspaceTopicMessages";
import { type ChatAction, type ConversationRef } from "../../state/chatState";

export type SendRealtimeCommandFn = (
  type: string,
  payload: Record<string, unknown>,
  id?: string,
) => boolean;

function peerMatchesSelection(peerId: string, sel: ConversationRef | null): boolean {
  if (!sel) return false;
  if (sel.kind === "dm") return sel.peerId === peerId;
  if (sel.kind === "vault") return peerId === vaultPeerKey(sel.vaultId);
  return workspaceTopicPeerKey(sel.workspaceId, sel.topicId) === peerId;
}

function patchChatIfViewing(
  dispatchChat: Dispatch<ChatAction> | null,
  getSelectedConversation: (() => ConversationRef | null) | null,
  peerId: string,
  messageId: string,
  patch: Partial<MessageRow>,
): void {
  if (!dispatchChat || !getSelectedConversation) return;
  if (!peerMatchesSelection(peerId, getSelectedConversation())) return;
  dispatchChat({ type: "patch-message", messageId, patch });
}

/**
 * Sends queued outbox entries in compose order. DM re-sends are idempotent (same message id).
 * Workspace skips re-send after `sentAtLeastOnce` until `workspace.msg.ack` removes the entry.
 */
export async function flushPendingOutbox(
  sendRealtimeCommand: SendRealtimeCommandFn,
  notifyMessagesChanged: (peerId: string) => void,
  dispatchChat: Dispatch<ChatAction> | null = null,
  getSelectedConversation: (() => ConversationRef | null) | null = null,
): Promise<void> {
  const entries = await listOutboxEntriesSorted();
  for (const entry of entries) {
    const fresh = await getOutboxEntry(entry.id);
    if (!fresh) continue;

    if (fresh.kind === "workspace-topic" && fresh.sentAtLeastOnce) {
      continue;
    }

    let sent = false;
    if (fresh.kind === "dm" && fresh.dmPayload) {
      sent = sendRealtimeCommand(
        "msg.send",
        {
          to: fresh.dmPayload.to,
          nonce: fresh.dmPayload.nonce,
          ciphertext: fresh.dmPayload.ciphertext,
          sender_nonce: fresh.dmPayload.sender_nonce,
          sender_ciphertext: fresh.dmPayload.sender_ciphertext,
          ...(fresh.dmPayload.attachment_blob_ids && fresh.dmPayload.attachment_blob_ids.length > 0
            ? { attachment_blob_ids: fresh.dmPayload.attachment_blob_ids }
            : {}),
        },
        fresh.id,
      );
    } else if (fresh.kind === "workspace-topic" && fresh.wsPayload) {
      sent = sendRealtimeCommand(
        "workspace.msg.send",
        {
          workspace_id: fresh.wsPayload.workspace_id,
          topic_id: fresh.wsPayload.topic_id,
          body: fresh.wsPayload.body,
          ...(fresh.wsPayload.reply_to ? { reply_to: fresh.wsPayload.reply_to } : {}),
        },
        fresh.id,
      );
    } else {
      continue;
    }

    if (!sent) {
      const nextAttempts = fresh.attempts + 1;
      await saveOutboxEntry({ ...fresh, attempts: nextAttempts });
      if (nextAttempts >= MAX_OUTBOX_FLUSH_ATTEMPTS) {
        await mergeMessagePatch(fresh.id, { failed: true, queued: true, pending: true });
        notifyMessagesChanged(fresh.peerId);
        patchChatIfViewing(dispatchChat, getSelectedConversation, fresh.peerId, fresh.id, {
          failed: true,
          queued: true,
          pending: true,
        });
      }
      break;
    }

    if (fresh.kind === "workspace-topic") {
      await saveOutboxEntry({ ...fresh, sentAtLeastOnce: true, attempts: 0 });
    }

    await mergeMessagePatch(fresh.id, { queued: false, failed: false });
    notifyMessagesChanged(fresh.peerId);
    patchChatIfViewing(dispatchChat, getSelectedConversation, fresh.peerId, fresh.id, {
      queued: false,
      failed: false,
    });
  }
}

export function useOutboxFlush(
  authenticated: boolean,
  conn: ConnectionState,
  sendRealtimeCommand: SendRealtimeCommandFn,
  notifyMessagesChanged: (peerId: string) => void,
  dispatchChat: Dispatch<ChatAction>,
  selectedConversationRef: MutableRefObject<ConversationRef | null>,
): void {
  const inFlight = useRef(false);
  const dispatchRef = useRef(dispatchChat);
  dispatchRef.current = dispatchChat;

  useEffect(() => {
    if (!authenticated || conn !== "open") return;
    let cancelled = false;
    void (async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        await flushPendingOutbox(
          sendRealtimeCommand,
          (peerId) => {
            if (!cancelled) notifyMessagesChanged(peerId);
          },
          dispatchRef.current,
          () => selectedConversationRef.current,
        );
      } finally {
        inFlight.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authenticated, conn, sendRealtimeCommand, notifyMessagesChanged, selectedConversationRef]);
}
